import React, {RefObject} from "react"
import IRoute from "model/interface/dataStorage/IRoute";
import {
    Button,
    Col,
    Collapse,
    Form,
    FormInstance,
    Input,
    Mentions,
    message,
    Row, Select,
    Spin,
    Switch,
    Tag,
    Tooltip
} from "antd";
import RoutesService from "../../../../model/service/ui/RoutesService";
import Wysiwyg from "../../../shared/input/Wysiwyg";
import ManualsService from "../../../../model/service/ui/ManualsService";
import IManual from "../../../../model/interface/ui/IManual";
import {reLoad, update} from "../../../../redux/actions/Setup";
import {connect, RootStateOrAny} from "react-redux";
import {ISetupState} from "../../../../redux/reducers/Setup";
import Modal from "../../../shared/modal/Modal";
import {CopyOutlined, SaveOutlined} from "@ant-design/icons";
import BackgroundPicker from "../../../shared/pickers/BackgroundPicker";
import LocaleText from "../../settings/dictionary/LocaleText";
import selectors from "../../../../redux/selectors";
import {API_TOKEN, API_TOKEN_LIST} from "../../../../model/constants/ApiConstant";
import Utils from "../../../../utils";

interface IProps {
    route?: IRoute
    onChange: (route?: IRoute) => void
    identifier?: string
    reLoad: () => void,
    update: (changes: any) => void,
    preventReload?: boolean,
    routes: IRoute[],
    modal?: boolean
    getText: (code: string, fallBack?: string) => string
}

interface IState {
    formRef: RefObject<FormInstance>,
    loading: boolean,
    loadingManual: boolean,
    manual?: IManual
}

class RouteForm extends React.Component<IProps, IState> {

    constructor(props: IProps) {
        super(props);
        this.state = {
            formRef: React.createRef(),
            loading: false,
            loadingManual: false,
        }
    }

    componentDidMount() {
        this.loadManual();
    }

    loadManual() {
        const {route} = this.props
        if (route && route.manual) {
            this.setState({loadingManual: true})
            ManualsService.resourceRetrieve(route.manual).then(manual => {
                this.setState({manual, loadingManual: false})
            })
        }
    }

    componentDidUpdate(prevProps: Readonly<IProps>) {
        if (prevProps.route?.uuid !== this.props.route?.uuid) {
            this.state.formRef.current?.resetFields()
            this.loadManual()
        }
    }

    onOk = () => {
        const {onChange, identifier, route, reLoad, update, preventReload, routes} = this.props
        const {formRef, manual} = this.state
        formRef.current?.validateFields()
            .then(values => {
                this.setState({loading: true})
                this.save({
                    ...route, ...values,
                    url: values.url + (identifier ? '/:' + identifier : ''),
                    manual: manual ? manual : null
                })
                    .then(resource => {
                        onChange(resource)
                        message.success('URL uložena. Aktualizuji osobní nastavení.').then()
                        preventReload ? update({
                            routes: [...routes.filter(r => r.uuid !== resource.uuid), resource]
                        }) : reLoad()
                    })
            })
    }

    save(route: IRoute) {
        if (route.id) {
            return RoutesService.resourceUpdate(route.id, route)
        }

        return RoutesService.collectionCreate(route)
    }

    onCancel = () => {
        this.props.onChange()
    }

    updateManual = (changes: any) => {
        this.setState(state => ({manual: {...state.manual, ...changes}}))
    }

    copyText = (text: string) => {
        navigator.clipboard.writeText(text)
            .then(() => message.success(this.props.getText('general.copied', 'Zkopírováno')).then())
    }

    render() {
        const {loading, formRef, loadingManual, manual} = this.state
        const {route, identifier, modal, getText} = this.props

        const rules = [
            {required: true, message: <LocaleText code={'form.item.required'}/>},
            () => ({
                validator(_: any, value: string) {
                    if (value.match(/^\/?([\w-:?&#]+\/?)*$/)) {
                        return Promise.resolve();
                    }
                    return Promise.reject(new Error(getText('configuration.route.url.invalid', 'Syntaxe adresy URL není platná.')));
                }
            })]

        const form = <div>
            <Form ref={formRef} initialValues={route} layout={"vertical"}>
                <Form.Item label={"URL"} name={"url"} rules={rules}>
                    <Input addonAfter={identifier ? '/:' + identifier : ''}/>
                </Form.Item>
                {(identifier || route?.url.match(/:([a-zA-Z_]+)/)) && (
                    <Form.Item label={<LocaleText code={'configuration.route.identifier-token'} fallback={'Nahradit identifikátor aktuální systémovou hodnotou'}/>} name={"identifierToken"}>
                        <Select allowClear={true} dropdownMatchSelectWidth={false}>
                            {API_TOKEN_LIST.filter(t => [API_TOKEN.USER, API_TOKEN.EMPLOYEE, API_TOKEN.UNIT, API_TOKEN.COMPANY, API_TOKEN.JOB, API_TOKEN.VIRTUAL_EMPLOYEE].includes(t.value || ''))
                                .map(token => {
                                    return (
                                        <Select.Option key={token.value || Utils.uuid()}
                                                       value={token.value || Utils.uuid()}>
                                            <LocaleText code={token.label} fallback={token.labelFallback}/>
                                        </Select.Option>
                                    )
                                })}
                        </Select>
                    </Form.Item>
                )}
                <Form.Item label={<LocaleText code={'general.background'} fallback={'Pozadí'}/>} name={"background"}>
                    <BackgroundPicker/>
                </Form.Item>
            </Form>
            <Collapse defaultActiveKey={route?.manual ? ['1'] : []}>
                <Collapse.Panel header={route?.manual ? 'Manuál' : 'Vytvořit manuál'} key="1">
                    {loadingManual ? <Row justify={"center"}><Spin size={"large"}/></Row> : (
                        <Form initialValues={manual} layout={"vertical"} onValuesChange={this.updateManual}>
                            <Form.Item label={"Obsah"} name={"content"}>
                                <Wysiwyg mode={'full'} inlineFile={true}/>
                            </Form.Item>
                            <Form.Item name={'active'} label={'Aktivní'} valuePropName={'checked'}>
                                <Switch/>
                            </Form.Item>
                        </Form>
                    )}
                </Collapse.Panel>
            </Collapse>
            {!modal && (
                <Button icon={<SaveOutlined/>} className={'mt-3'} type="primary" loading={loading} onClick={this.onOk}>
                    <LocaleText code={'general.save'}/>
                </Button>
            )}
        </div>

        return modal ? (<Modal
                title={route ? <LocaleText code={'configuration.route.edit'} fallback={'Upravit cestu'}/> : <LocaleText code={'configuration.route.create'} fallback={'Vytvořit cestu'}/>}
                visible={true}
                fullScreenOption={true}
                destroyOnClose={true}
                onOk={() => this.onOk()}
                onCancel={this.onCancel}
                closable={true}
                footer={[
                    <Button key="submit" type="primary" loading={loading} onClick={this.onOk} icon={<SaveOutlined/>}>
                        <LocaleText code={'general.save'}/>
                    </Button>
                ]}
            >
                {form}
            </Modal>
        ) : form
    }
}


const mapStateToProps = (state: RootStateOrAny) => {
    const {routes} = state.setup as ISetupState
    return {
        routes,
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        reLoad: () => dispatch(reLoad()),
        update: (changes: any) => dispatch(update(changes))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(RouteForm)
